/**
 * SchemaArrayTable — renders an array of objects as a dynamic-column table,
 * driven by JSON Schema. Supports inline editing, add/delete rows, sorting,
 * copy-to-chat, and smart overlays.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getTableColumns, getFieldFormat, getFieldType, getFieldEnum, getEnumLabel, isPageLink, isFieldMasked, isFieldReadOnly, getOverlayType, createDefaultObject, getArrayItemSchema, getFieldLabel, maskValue } from '../../utils/schemaUtils';
import { getOverlay } from './overlays';
import ConfirmBeforeActionDialog from '../ui/dialog-confirm-before-action';
import styles from './SchemaArrayTable.module.css';

function SchemaArrayTable({ items = [], arraySchema, parentData, rootData, onChange, onInteraction, readOnly = false }) {
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [editingCell, setEditingCell] = useState(null);
  const [revealedCells, setRevealedCells] = useState(new Set());
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState(null);
  // Pending date edit awaiting reviewer confirmation when the new date falls
  // outside the parent statement's begin/end window. Only used for fields
  // whose schema declares x-warn-outside-statement-period: true (typically
  // posting_date and transaction_date).
  const [pendingDateConfirm, setPendingDateConfirm] = useState(null);

  const columns = useMemo(() => getTableColumns(arraySchema), [arraySchema]);
  const itemSchema = getArrayItemSchema(arraySchema);
  const overlayType = getOverlayType(arraySchema);
  const overlay = overlayType ? getOverlay(overlayType) : null;

  // Sort
  const sorted = useMemo(() => {
    if (!sortField) return items;
    return [...items].sort((a, b) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      const format = columns.find(c => c.key === sortField)?.schema;
      const fType = getFieldType(format);
      if (fType === 'number' || fType === 'integer') {
        return sortDir === 'asc' ? (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0) : (parseFloat(bVal) || 0) - (parseFloat(aVal) || 0);
      }
      return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
  }, [items, sortField, sortDir, columns]);

  // Computed overlay column
  const overlayBalances = useMemo(() => {
    if (!overlay?.computeColumn) return null;
    return overlay.computeColumn(parentData, sorted);
  }, [overlay, parentData, sorted]);

  // Header click for sorting
  const handleHeaderClick = (key) => {
    if (key === sortField) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortField(null); setSortDir('asc'); }
    } else {
      setSortField(key);
      setSortDir('asc');
    }
  };

  // Returns true if a date string falls outside [begin_date - tol, end_date + tol]
  // of the parent statement, where tol = 5 days. Returns false if any reference
  // is missing (no warning to issue) or if the value is empty (clearing a
  // nullable field is allowed without a warning).
  const isDateOutsideStatementPeriod = useCallback((isoDate) => {
    const TOLERANCE_DAYS = 5;
    if (!isoDate) return false;
    const beginStr = parentData?.begin_date;
    const endStr = parentData?.end_date;
    if (!beginStr || !endStr) return false;
    const tolMs = TOLERANCE_DAYS * 24 * 60 * 60 * 1000;
    const v = new Date(isoDate).getTime();
    const b = new Date(beginStr).getTime() - tolMs;
    const e = new Date(endStr).getTime() + tolMs;
    if (Number.isNaN(v) || Number.isNaN(b) || Number.isNaN(e)) return false;
    return v < b || v > e;
  }, [parentData]);

  // Apply a committed cell edit to the items array (no validation — the caller
  // is responsible for ensuring `finalVal` is the desired value).
  const applyCommittedEdit = useCallback((rowIdx, fieldKey, finalVal) => {
    const originalIdx = items.indexOf(sorted[rowIdx]);
    if (originalIdx < 0) return;
    const newItems = items.map((item, i) => i === originalIdx ? { ...item, [fieldKey]: finalVal } : item);
    onChange(newItems);
    setEditingCell(null);
  }, [items, sorted, onChange]);

  // Cell editing
  const commitEdit = useCallback((rowIdx, fieldKey, value) => {
    const col = columns.find(c => c.key === fieldKey);
    const fType = getFieldType(col?.schema);
    const fmt = getFieldFormat(col?.schema);
    let finalVal = value;
    if (fType === 'number' || fType === 'integer' || fmt === 'currency') {
      finalVal = parseFloat(value) || 0;
    }
    if (fmt === 'date') {
      // Validate and normalize date input
      const trimmed = (value || '').trim();
      if (!trimmed) {
        finalVal = '';
      } else {
        // Accept YYYY-MM-DD or M/D/YYYY
        const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        let parsed = null;
        if (isoMatch) {
          parsed = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
        } else if (usMatch) {
          parsed = new Date(parseInt(usMatch[3]), parseInt(usMatch[1]) - 1, parseInt(usMatch[2]));
        }
        if (!parsed || isNaN(parsed.getTime())) {
          alert('Invalid date. Use YYYY-MM-DD or M/D/YYYY format.');
          setEditingCell(null);
          return false;
        }
        // Store as YYYY-MM-DD (ISO format for S3)
        const yyyy = parsed.getFullYear();
        const mm = String(parsed.getMonth() + 1).padStart(2, '0');
        const dd = String(parsed.getDate()).padStart(2, '0');
        finalVal = `${yyyy}-${mm}-${dd}`;
      }
    }
    // If this is a date field flagged with x-warn-outside-statement-period
    // and the new value is outside the parent statement's window, ask the
    // reviewer to confirm before committing. Catches typos like 2026 vs 2025.
    // Returns false so callers (e.g., Tab-to-next-cell) know not to advance.
    const warnOutside = col?.schema?.['x-warn-outside-statement-period'] === true;
    if (warnOutside && fmt === 'date' && finalVal && isDateOutsideStatementPeriod(finalVal)) {
      setPendingDateConfirm({
        rowIdx,
        fieldKey,
        finalVal,
        fieldLabel: col?.label || fieldKey,
        beginDate: parentData?.begin_date,
        endDate: parentData?.end_date,
      });
      return false;
    }
    applyCommittedEdit(rowIdx, fieldKey, finalVal);
    return true;
  }, [items, sorted, columns, onChange, applyCommittedEdit, isDateOutsideStatementPeriod, parentData]);

  // Add row
  const handleAddRow = useCallback(() => {
    const newItem = createDefaultObject(itemSchema);
    onChange([...items, newItem]);
  }, [items, itemSchema, onChange]);

  // Insert row after a specific position
  const handleInsertRow = useCallback((rowIdx) => {
    const newItem = createDefaultObject(itemSchema);
    const originalIdx = items.indexOf(sorted[rowIdx]);
    if (originalIdx < 0) {
      onChange([...items, newItem]);
    } else {
      const newItems = [...items];
      newItems.splice(originalIdx + 1, 0, newItem);
      onChange(newItems);
    }
  }, [items, sorted, itemSchema, onChange]);

  // Delete row — show confirmation dialog
  const handleDeleteRow = useCallback((rowIdx) => {
    setPendingDeleteIdx(rowIdx);
  }, []);

  const confirmDeleteRow = useCallback(() => {
    if (pendingDeleteIdx === null) return;
    const originalIdx = items.indexOf(sorted[pendingDeleteIdx]);
    if (originalIdx >= 0) {
      onChange(items.filter((_, i) => i !== originalIdx));
    }
    setPendingDeleteIdx(null);
  }, [pendingDeleteIdx, items, sorted, onChange]);

  // Copy to chat
  const handleCopyToChat = useCallback((rowIdx) => {
    const row = sorted[rowIdx];
    const parts = columns
      .filter(c => !c.internal)
      .map(c => {
        const val = row[c.key];
        if (val === null || val === undefined || val === '') return null;
        const fmt = getFieldFormat(c.schema);
        const formatted = fmt === 'currency' ? formatCurrency(val) : fmt === 'date' ? formatDate(val) : String(val);
        return `${c.label}: ${formatted}`;
      })
      .filter(Boolean);
    const message = `Row ${rowIdx + 1} [${row.id || ''}]: ${parts.join(' | ')}`;
    onInteraction?.({ type: 'copy_to_chat', message, data: row, transactionId: row.id });
  }, [sorted, columns, onInteraction]);

  // Tab navigation between editable cells
  const moveToNextCell = useCallback((rowIdx, colKey, reverse = false) => {
    const colIdx = columns.findIndex(c => c.key === colKey);
    if (colIdx < 0) return;
    const dir = reverse ? -1 : 1;
    let nextCol = colIdx + dir;
    let nextRow = rowIdx;
    // Wrap to next/prev row
    if (nextCol >= columns.length) { nextCol = 0; nextRow++; }
    if (nextCol < 0) { nextCol = columns.length - 1; nextRow--; }
    // Bounds check
    if (nextRow >= 0 && nextRow < sorted.length) {
      setEditingCell({ rowIdx: nextRow, key: columns[nextCol].key });
    } else {
      setEditingCell(null);
    }
  }, [columns, sorted.length]);

  // Row click -> page navigation + highlight
  const handleRowClick = useCallback((rowIdx) => {
    const row = sorted[rowIdx];
    setSelectedRowId(row.id || rowIdx);
    onInteraction?.({
      type: 'transaction_selected',
      transactionId: row.id,
      data: {
        ...row,
        date: row.posting_date || row.transaction_date,
      },
    });
  }, [sorted, onInteraction]);

  // Format cell value
  const formatCell = (value, colSchema) => {
    if (value === null || value === undefined || value === '') return '';
    const fmt = getFieldFormat(colSchema);
    if (fmt === 'currency') return formatCurrency(value);
    if (fmt === 'date') {
      try { return formatDate(value); } catch { return String(value); }
    }
    if (getFieldEnum(colSchema)) return getEnumLabel(colSchema, value);
    return String(value);
  };

  return (
    <div className={styles.container}>
      {/* Overlay summary bar */}
      {overlay?.SummaryComponent && (
        <overlay.SummaryComponent
          parentData={parentData}
          rows={sorted}
          securities={parentData?.securities}
          cashPositions={parentData?.cash_positions}
        />
      )}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        {!readOnly && <button className={styles.addBtn} onClick={handleAddRow}>+ Add Row</button>}
        <span className={styles.rowCount}>{items.length} row(s)</span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thNarrow}></th>
              <th className={styles.thNarrow}>#</th>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`${styles.th} ${getFieldFormat(col.schema) === 'currency' ? styles.thRight : ''}`}
                  onClick={() => handleHeaderClick(col.key)}
                >
                  {col.label}
                  {sortField === col.key && (
                    <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
                  )}
                </th>
              ))}
              {overlay?.columnLabel && (
                <th className={`${styles.th} ${styles.thRight}`}>{overlay.columnLabel}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, idx) => (
              <tr key={row.id || idx} className={`${styles.tr} ${(row.id || idx) === selectedRowId ? styles.selected : ''}`} onClick={() => handleRowClick(idx)}>
                <td className={styles.tdAction}>
                  <button className={styles.copyBtn} onClick={(e) => { e.stopPropagation(); handleCopyToChat(idx); }} title="Send to chat">💬</button>
                  {!readOnly && (
                    <>
                      <button className={styles.insertBtn} onClick={(e) => { e.stopPropagation(); handleInsertRow(idx); }} title="Insert row after" aria-label="Insert row after">+</button>
                      <button className={styles.deleteBtn} onClick={(e) => { e.stopPropagation(); handleDeleteRow(idx); }} title="Remove" aria-label="Remove row">✕</button>
                    </>
                  )}
                </td>
                <td className={styles.tdRowNum}>{idx + 1}</td>
                {columns.map(col => {
                  const isEditing = editingCell?.rowIdx === idx && editingCell?.key === col.key;
                  const value = row[col.key];
                  const fmt = getFieldFormat(col.schema);
                  const fType = getFieldType(col.schema);
                  const enumVals = getFieldEnum(col.schema);

                  if (isEditing) {
                    if (enumVals) {
                      return (
                        <td key={col.key} className={styles.td}>
                          <select
                            autoFocus
                            className={styles.editInput}
                            defaultValue={value || ''}
                            onChange={(e) => commitEdit(idx, col.key, e.target.value)}
                            onBlur={() => setEditingCell(null)}
                          >
                            <option value="">—</option>
                            {enumVals.map(v => <option key={v} value={v}>{getEnumLabel(col.schema, v)}</option>)}
                          </select>
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} className={styles.td}>
                        <input
                          autoFocus
                          className={styles.editInput}
                          type="text"
                          defaultValue={value ?? ''}
                          onBlur={(e) => { if (!e.relatedTarget?.classList?.contains(styles.editInput)) commitEdit(idx, col.key, e.target.value); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit(idx, col.key, e.target.value);
                            if (e.key === 'Escape') setEditingCell(null);
                            if (e.key === 'Tab') {
                              e.preventDefault();
                              const committed = commitEdit(idx, col.key, e.target.value);
                              // If commit was deferred (e.g., date confirmation
                              // dialog opened), don't advance — the reviewer
                              // needs to resolve the dialog first.
                              if (committed) moveToNextCell(idx, col.key, e.shiftKey);
                            }
                          }}
                        />
                      </td>
                    );
                  }

                  const cellClass = `${fmt === 'currency' ? styles.tdAmount : styles.td}`;
                  const pageLink = isPageLink(col.schema);
                  const masked = isFieldMasked(col.schema);
                  const cellKey = `${idx}-${col.key}`;
                  const isRevealed = revealedCells.has(cellKey);

                  return (
                    <td
                      key={col.key}
                      className={cellClass}
                      onDoubleClick={(readOnly || isFieldReadOnly(col.schema)) ? undefined : (e) => { e.stopPropagation(); setEditingCell({ rowIdx: idx, key: col.key }); }}
                      title={isFieldReadOnly(col.schema) ? undefined : "Double-click to edit"}
                    >
                      {pageLink && value ? (
                        <span className={styles.pageLink}>{value}</span>
                      ) : masked && !isRevealed ? (
                        <span className={styles.maskedValue}>
                          {maskValue(value)}
                          <button
                            className={styles.revealBtn}
                            onClick={(e) => { e.stopPropagation(); setRevealedCells(prev => new Set([...prev, cellKey])); }}
                            title="Reveal"
                          >👁</button>
                        </span>
                      ) : (
                        <>
                          {formatCell(value, col.schema)}
                          {masked && isRevealed && (
                            <button
                              className={styles.revealBtn}
                              onClick={(e) => { e.stopPropagation(); setRevealedCells(prev => { const next = new Set(prev); next.delete(cellKey); return next; }); }}
                              title="Hide"
                            >🔒</button>
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
                {overlayBalances && (
                  <td className={styles.tdAmount}>{formatCurrency(overlayBalances[idx])}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmBeforeActionDialog
        open={pendingDeleteIdx !== null}
        title="Remove Row"
        confirmMessage={pendingDeleteIdx !== null && sorted[pendingDeleteIdx]
          ? `Remove row ${pendingDeleteIdx + 1}: ${sorted[pendingDeleteIdx].description || sorted[pendingDeleteIdx].posting_date || 'this item'}?`
          : 'Remove this row?'}
        proceedButtonLabel="Remove"
        cancelButtonLabel="Cancel"
        onProceed={confirmDeleteRow}
        onCancel={() => setPendingDeleteIdx(null)}
        list_element={() => null}
      />
      <ConfirmBeforeActionDialog
        open={pendingDateConfirm !== null}
        title="Date Outside Statement Period"
        confirmMessage={pendingDateConfirm
          ? `You entered ${pendingDateConfirm.fieldLabel}: ${formatDate(pendingDateConfirm.finalVal)}, but this statement covers ${formatDate(pendingDateConfirm.beginDate)} – ${formatDate(pendingDateConfirm.endDate)}. Some issuers (e.g., Amex) legitimately backdate transactions by months or even years. Apply this date anyway?`
          : ''}
        proceedButtonLabel="Yes, the date is correct"
        cancelButtonLabel="Cancel — let me re-enter"
        onProceed={() => {
          if (pendingDateConfirm) {
            applyCommittedEdit(pendingDateConfirm.rowIdx, pendingDateConfirm.fieldKey, pendingDateConfirm.finalVal);
          }
          setPendingDateConfirm(null);
        }}
        onCancel={() => {
          // Keep the cell in edit mode so the reviewer can fix the typo
          if (pendingDateConfirm) {
            setEditingCell({ rowIdx: pendingDateConfirm.rowIdx, key: pendingDateConfirm.fieldKey });
          }
          setPendingDateConfirm(null);
        }}
        list_element={() => null}
      />
    </div>
  );
}

export default SchemaArrayTable;
